import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Checkbox, ConfirmDialog, Drawer } from "@outpave/ds";
import { Button, FormControlLabel, Typography } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import { useFormik, setNestedObjectValues } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { useAuth } from "@frontegg/react";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import type { Employee } from "../cards/EmployeeSearch";
import { GET_USER, ADD_USER } from "../../../graphql/user";
import DisplayUserInformation from "components/common/users/DisplayUserInformation";
import UserInformation from "components/common/users/UserInformation";
import StatusType from "../../../enums/UserStatus";
import { toast } from "react-toastify";
import { FormikAddUser } from "hooks/useUserProfileData";
import { generateUserValidationSchema } from "../../../validations/users";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import dayjs from "dayjs";
import { useLogoForScheme } from "utils/themeContextHelper";
import { useUserMentions } from "context/UserMentionProvider";

interface AddNewUserDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  handleCreatedUser: (error?: string | null, user?: Employee) => void;
}

/**
 * The created user, in the shape the one consumer that reads it wants. Card
 * issuance needs `phone` and `birthdate`, so dropping either here makes
 * EmployeeSearch's "missing information" notice permanent.
 */
const employeeFromForm = (values: FormikAddUser, id: string): Employee => ({
  id,
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  phone: values.phone,
  birthdate: values.birthdate,
  address: values.address,
  hasPhysicalCard: values.hasPhysicalCard,
});

const AddNewUserDrawer: React.FC<AddNewUserDrawerProps> = (props) => {
  const { handleCreatedUser, open, handleCloseDrawer } = props;
  const { refetch: refetchGlobalUsers } = useUserMentions();

  const logo = useLogoForScheme();
  const { user } = useAuth();

  const [step, setStep] = useState<number>(0);
  const [openCreateDrawer, setOpenCreateDrawer] = useState<boolean>(false);
  const [openCancelUserCreation, setOpenCancelUserCreation] =
    useState<boolean>(false);

  const {
    customFieldSettings,
    customFieldsInitialValues,
    transformCustomFieldsToArray,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.USER,
  });

  const validationSchema = generateUserValidationSchema(customFieldSettings);

  const formikAddUser = useFormik<FormikAddUser>({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      employeeGroup: "",
      phone: "",
      status: StatusType.PENDING,
      address: {
        addressName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipcode: "",
        country: "US",
      },
      location: {
        id: "",
        addressName: "",
      },
      assignedManager: {
        id: "",
        firstName: "",
        lastName: "",
        email: "",
      },
      userPassword: "",
      role: "",
      sendOneTimePassword: true,
      createAndAssignNewCard: false,
      birthdate: null,
      customFields: customFieldsInitialValues,
      company: {
        id: "",
        businessLegalName: "",
      },
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const {
        firstName,
        lastName,
        role,
        email,
        phone,
        status,
        address,
        assignedManager,
        sendOneTimePassword,
        birthdate,
        customFields,
        location,
        createAndAssignNewCard,
        companyId,
      } = values;

      addUser({
        variables: {
          input: {
            companyId: companyId || userData?.user?.company?.id,
            departmentId: userData?.user?.department?.id,
            firstName: firstName,
            middleName: "",
            lastName: lastName,
            title: "Other",
            role: role,
            isOwner: false,
            email: email,
            phone: phone,
            status: status,
            address: {
              addressName: address.addressName,
              address1: address.address1,
              address2: address.address2,
              city: address.city,
              state: address.state,
              zipcode: address.zipcode.toString(),
              country: "US",
            },
            location: {
              id: location.id,
            },
            managerId: assignedManager.id,
            sendOneTimePassword: sendOneTimePassword,
            birthdate: dayjs(birthdate).valueOf(),
            customFields: transformCustomFieldsToArray(customFields),
          },
        },
      });
    },
  });

  const { data: userData } = useQuery(GET_USER, {
    variables: { userId: user?.id },
    skip: Boolean(!user?.id),
  });

  const [addUser, { loading }] = useMutation(ADD_USER, {
    onCompleted: ({ addUser }) => {
      setStep(0);

      const updatedUser = {
        ...formikAddUser.values,
        id: addUser.id,
      };

      if (formikAddUser.values.createAndAssignNewCard) {
        formikAddUser.setValues(updatedUser);
        setOpenCreateDrawer(true);
      } else {
        formikAddUser.resetForm();
        handleCreatedUser(null, employeeFromForm(updatedUser, addUser.id));
        handleCloseDrawer();
      }
      refetchGlobalUsers();
      toast.success(t("User created successfully!"));
    },
    onError: (e) => {
      handleCreatedUser(e.message);
      toast.error(t("Failed to create user") + ": " + e.message);
    },
  });

  const { t } = useTranslation();

  const handleCloseCancelUserCreationModal = () => {
    setOpenCancelUserCreation(false);
  };

  const handleReset = () => {
    setStep(0);

    formikAddUser.resetForm();
    handleCloseDrawer();
  };

  const handleCloseCreateDrawer = () => {
    // Read the form before resetting it: this path ran after the card drawer
    // closed, and it was handing back `addUser`, the mutation trigger.
    const created = employeeFromForm(
      formikAddUser.values,
      formikAddUser.values.id ?? "",
    );
    formikAddUser.resetForm();
    handleCreatedUser(null, created);
    handleCloseDrawer();
    setOpenCreateDrawer(false);
  };

  // Each step kept its own close behaviour: step 0 closed outright, step 1
  // asked. The DS has one close path, so it branches here instead. The
  // backdrop used to reset without asking; it now follows the X.
  const handleRequestClose = () => {
    if (step === 0) {
      handleCloseDrawer();
      return;
    }
    setOpenCancelUserCreation(true);
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleRequestClose}
        title={t("Add User")}
        loading={loading}
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={() => {
                setOpenCancelUserCreation(true);
              }}
            >
              {t("Cancel")}
            </Button>

            {step === 1 && (
              <Button
                variant="primary-soft"
                onClick={async () => {
                  setStep(step - 1);
                }}
              >
                {t("Back")}
              </Button>
            )}

            {step === 0 ? (
              <Button
                variant="primary-filled"
                onClick={async () => {
                  const errors = await formikAddUser.validateForm();
                  if (Object.keys(errors).length !== 0) {
                    formikAddUser.setTouched(
                      setNestedObjectValues(errors, true),
                    );
                    return;
                  }
                  setStep(step + 1);
                }}
              >
                {t("Next")}
              </Button>
            ) : (
              <Button
                variant="primary-filled"
                onClick={() => formikAddUser.handleSubmit()}
              >
                {t("Create")}
              </Button>
            )}
          </>
        }
      >
        {step === 0 && (
          <UserInformation
            formik={formikAddUser}
            customFieldSettings={customFieldSettings}
          />
        )}
        {step === 1 && (
          <form onSubmit={formikAddUser.handleSubmit}>
            <DisplayUserInformation
              handleClickEdit={() => {
                setStep(0);
              }}
              formik={formikAddUser}
              includeIdentity
            />
            <Stack gap="var(--space-4)" sx={{ marginTop: "var(--space-9)" }}>
              <FormControlLabel
                checked={formikAddUser.values.sendOneTimePassword}
                control={
                  <Checkbox
                    value={formikAddUser.values.sendOneTimePassword}
                    name="sendOneTimePassword"
                    onChange={formikAddUser.handleChange}
                  />
                }
                label={
                  <Typography variant="label" component="span">
                    {t(
                      "Send one-time password and notify new employee of their invitation to",
                    )}
                    <img
                      src={logo}
                      alt="Company Logo"
                      style={{
                        height: 16,
                        width: "auto",
                        marginLeft: 8,
                        marginBottom: 2,
                        display: "inline-block",
                        verticalAlign: "middle",
                      }}
                      draggable={false}
                    />
                  </Typography>
                }
              />
              <FormControlLabel
                checked={formikAddUser.values.createAndAssignNewCard}
                control={
                  <Checkbox
                    value={formikAddUser.values.createAndAssignNewCard}
                    name="createAndAssignNewCard"
                    onChange={(value) => {
                      formikAddUser.handleChange(value);
                    }}
                  />
                }
                label={
                  <Typography variant="label" component="span">
                    {t("Create and Assign New Card")}
                  </Typography>
                }
              />
            </Stack>
          </form>
        )}
      </Drawer>
      <ConfirmDialog
        open={openCancelUserCreation}
        title={t("Cancel User Creation")}
        tone="primary"
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        message={
          <Trans
            i18nKey="CancelUserCreationDescription"
            components={{
              canceling_text: (
                <Typography
                  component="span"
                  variant="inherit"
                  sx={{ fontWeight: "var(--type-weight-medium)" }}
                />
              ),
            }}
          />
        }
        onCancel={handleCloseCancelUserCreationModal}
        onConfirm={() => {
          handleReset();
          handleCloseCancelUserCreationModal();
        }}
      />

      {openCreateDrawer && formikAddUser.values.id && (
        <CardFormDrawer
          open={openCreateDrawer}
          cardholder={{
            id: formikAddUser.values.id,
            firstName: formikAddUser.values.firstName,
            lastName: formikAddUser.values.lastName,
            email: formikAddUser.values.email,
            address: formikAddUser.values.address,
            phone: formikAddUser.values.phone,
            birthdate: formikAddUser.values.birthdate,
            hasPhysicalCard: false,
          }}
          onClose={handleCloseCreateDrawer}
        />
      )}
    </>
  );
};

export default AddNewUserDrawer;
